import React, { useState } from 'react';
import { projects } from '../data/portfolioData';
import './Projects.css';

// ── Animated SVG banners per project category ─────────────────────────────────
function ProjectBanner({ project }) {
  const { category, accentColor, id } = project;

  if (id === 'gpu-compute') return (
    <svg viewBox="0 0 600 200" className="proj-banner" aria-hidden="true">
      <defs>
        <linearGradient id={`g-${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#040d1e"/>
          <stop offset="100%" stopColor="#0a1f3d"/>
        </linearGradient>
      </defs>
      <rect width="600" height="200" fill={`url(#g-${id})`}/>
      {/* Grid lines */}
      {[0,1,2,3,4,5].map(i=><line key={i} x1={i*120} y1="0" x2={i*120} y2="200" stroke="#0EA5E9" strokeOpacity=".08"/>)}
      {[0,1,2,3].map(i=><line key={i} x1="0" y1={i*66} x2="600" y2={i*66} stroke="#0EA5E9" strokeOpacity=".08"/>)}
      {/* GPU nodes */}
      {[[80,60],[200,110],[320,55],[440,115],[560,70],[140,150],[380,155]].map(([cx,cy],i)=>(
        <g key={i}><circle cx={cx} cy={cy} r="18" fill="none" stroke="#0EA5E9" strokeOpacity=".5" strokeWidth="1.5"/>
        <circle cx={cx} cy={cy} r="6" fill="#0EA5E9" fillOpacity=".8">
          <animate attributeName="r" values="6;8;6" dur={`${1.5+i*0.3}s`} repeatCount="indefinite"/>
        </circle></g>
      ))}
      {/* Connection lines */}
      {[[80,60,200,110],[200,110,320,55],[320,55,440,115],[440,115,560,70],[200,110,140,150],[320,55,380,155]].map(([x1,y1,x2,y2],i)=>(
        <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#0EA5E9" strokeOpacity=".25" strokeWidth="1" strokeDasharray="4 4"/>
      ))}
      <text x="300" y="105" textAnchor="middle" fill="#0EA5E9" fillOpacity=".12" fontFamily="monospace" fontSize="52" fontWeight="bold">GPU</text>
    </svg>
  );

  if (id === 'web3-ai-platform') return (
    <svg viewBox="0 0 600 200" className="proj-banner" aria-hidden="true">
      <defs>
        <linearGradient id={`g-${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#1a0a2e"/>
          <stop offset="100%" stopColor="#2d0a20"/>
        </linearGradient>
      </defs>
      <rect width="600" height="200" fill={`url(#g-${id})`}/>
      {/* Brain wave lines */}
      {[60,100,140].map((y,i)=>(
        <polyline key={i} points={`0,${y} 80,${y} 100,${y-20} 120,${y+20} 140,${y-15} 160,${y+15} 180,${y} 600,${y}`}
          fill="none" stroke="#E84393" strokeOpacity={0.15+i*.08} strokeWidth="1.5"/>
      ))}
      {/* AI nodes */}
      {[[120,100],[240,70],[360,130],[480,90],[300,160]].map(([cx,cy],i)=>(
        <g key={i}>
          <circle cx={cx} cy={cy} r="14" fill="#E84393" fillOpacity=".12"/>
          <circle cx={cx} cy={cy} r="5"  fill="#E84393" fillOpacity=".7">
            <animate attributeName="opacity" values=".7;1;.7" dur={`${1.2+i*0.25}s`} repeatCount="indefinite"/>
          </circle>
        </g>
      ))}
      <text x="300" y="110" textAnchor="middle" fill="#E84393" fillOpacity=".08" fontFamily="monospace" fontSize="48" fontWeight="bold">AI·BOT</text>
    </svg>
  );

  if (id === 'multisig-vault') return (
    <svg viewBox="0 0 600 200" className="proj-banner" aria-hidden="true">
      <defs>
        <linearGradient id={`g-${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0f0624"/>
          <stop offset="100%" stopColor="#1a0a40"/>
        </linearGradient>
      </defs>
      <rect width="600" height="200" fill={`url(#g-${id})`}/>
      {/* Vault circle */}
      <circle cx="300" cy="100" r="70" fill="none" stroke="#7C3AED" strokeOpacity=".3" strokeWidth="2"/>
      <circle cx="300" cy="100" r="50" fill="none" stroke="#7C3AED" strokeOpacity=".2" strokeWidth="1.5"/>
      {/* Key signers */}
      {[0,1,2,3,4].map(i=>{
        const a=(i*72-90)*Math.PI/180;
        const cx=300+72*Math.cos(a), cy=100+72*Math.sin(a);
        return (<g key={i}>
          <line x1="300" y1="100" x2={cx} y2={cy} stroke="#7C3AED" strokeOpacity=".25" strokeDasharray="3 3"/>
          <circle cx={cx} cy={cy} r="10" fill="#7C3AED" fillOpacity=".2" stroke="#7C3AED" strokeOpacity=".5" strokeWidth="1.5"/>
          <text x={cx} y={cy+4} textAnchor="middle" fill="#a78bfa" fontSize="8" fontFamily="monospace">✓</text>
        </g>);
      })}
      <text x="300" y="108" textAnchor="middle" fill="#7C3AED" fillOpacity=".6" fontFamily="monospace" fontSize="12">MULTISIG</text>
    </svg>
  );

  if (id === 'blockchain-analytics') return (
    <svg viewBox="0 0 600 200" className="proj-banner" aria-hidden="true">
      <defs>
        <linearGradient id={`g-${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#041a10"/>
          <stop offset="100%" stopColor="#0a2820"/>
        </linearGradient>
      </defs>
      <rect width="600" height="200" fill={`url(#g-${id})`}/>
      {/* Bar chart */}
      {[40,80,55,120,90,65,140,100,75,110,45,130].map((h,i)=>(
        <rect key={i} x={20+i*46} y={170-h} width="30" height={h}
          fill="#10b981" fillOpacity={.15+i*.025} rx="3">
          <animate attributeName="height" from="0" to={h} dur={`${0.4+i*0.06}s`} fill="freeze"/>
          <animate attributeName="y" from="170" to={170-h} dur={`${0.4+i*0.06}s`} fill="freeze"/>
        </rect>
      ))}
      {/* Trend line */}
      <polyline points="35,130 81,90 127,115 173,50 219,80 265,55 311,30 357,70 403,45 449,60 495,80 541,40"
        fill="none" stroke="#10b981" strokeOpacity=".7" strokeWidth="2"/>
      <text x="300" y="110" textAnchor="middle" fill="#10b981" fillOpacity=".08" fontFamily="monospace" fontSize="48" fontWeight="bold">DATA</text>
    </svg>
  );

  if (id === 'toy-nft') return (
    <svg viewBox="0 0 600 200" className="proj-banner" aria-hidden="true">
      <defs>
        <linearGradient id={`g-${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#1a0d00"/>
          <stop offset="100%" stopColor="#2a1500"/>
        </linearGradient>
      </defs>
      <rect width="600" height="200" fill={`url(#g-${id})`}/>
      {/* NFT cards */}
      {[[60,50],[160,65],[260,45],[360,65],[460,50],[510,120],[110,130],[210,120],[310,130],[410,120]].map(([x,y],i)=>(
        <rect key={i} x={x} y={y} width="60" height="80" rx="8"
          fill="none" stroke="#f6851b" strokeOpacity={.15+i*.04} strokeWidth="1.5"/>
      ))}
      {/* Hex ETH logo centre */}
      <text x="300" y="115" textAnchor="middle" fill="#f6851b" fillOpacity=".6" fontSize="50">⬡</text>
      <text x="300" y="118" textAnchor="middle" fill="#fff" fillOpacity=".5" fontFamily="monospace" fontSize="14" fontWeight="bold">NFT</text>
    </svg>
  );

  // Fallback
  return (
    <div className="proj-banner-fallback" style={{ background:`linear-gradient(135deg, #111, #222)` }}>
      <span style={{ fontSize:'3rem' }}>📦</span>
    </div>
  );
}

function TechTag({ tech, colors }) {
  const s = colors[tech] || { bg:'var(--bg2)', color:'var(--txt2)' };
  return <span className="ptag" style={{ background:s.bg, color:s.color }}>{tech}</span>;
}

function ProjectCard({ project }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="proj-card" style={{ gridColumn:`span ${project.span}` }}>
      {/* Interactive SVG banner */}
      <div className="proj-banner-wrap">
        <ProjectBanner project={project} />
        <div className="proj-banner-badges">
          {project.nda && <span className="badge-nda">🔒 NDA</span>}
          <span className={`badge-status badge-${project.status === 'Production' ? 'prod' : 'dev'}`}>
            {project.status === 'Production' ? '🟢' : '🔵'} {project.status}
          </span>
          <span className="badge-co">{project.company}</span>
        </div>
      </div>

      <div className="proj-body">
        <div className="proj-tech">
          {project.tech.map(t => <TechTag key={t} tech={t} colors={project.techColors} />)}
        </div>

        <h3 className="proj-title">{project.title}</h3>
        <p className="proj-desc">{project.desc}</p>

        <ul className={`proj-highlights${expanded ? ' expanded' : ''}`}>
          {project.highlights.map((h,i) => (
            <li key={i}><span className="hl-dot">▸</span>{h}</li>
          ))}
        </ul>

        <div className="proj-footer">
          {project.highlights.length > 3 && (
            <button className="proj-expand" onClick={() => setExpanded(e => !e)}>
              {expanded ? '▲ Show less' : `▾ +${project.highlights.length - 3} more`}
            </button>
          )}
          {project.confidential && (
            <span className="proj-nda-note">🔒 Details shared under NDA</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Projects() {
  return (
    <section id="projects">
      <div className="section-label">Projects</div>
      <h2 className="section-title">Selected Work</h2>
      <p className="section-sub">
        Production systems built across TechThree and Chainz Experts. Confidential projects can be discussed in detail during interviews.
      </p>
      <div className="projects-grid">
        {projects.map(p => <ProjectCard key={p.id} project={p} />)}
      </div>
    </section>
  );
}
